// visual_edit — la géométrie des trois gestes qui déplacent et redimensionnent une photo.
//
// Demande de l'opérateur, le 2026-10-05 : « enable user to change the app look visually […]
// place a photo somewhere », « on veut cliquer deplacer et on veut aussi que ca ne nique pas
// la responsivité », « you should also be able to resize the images ».
//
// ── LA RÈGLE QUI TIENT TOUT : AUCUN PIXEL NE SURVIT AU GESTE ────────────────
//
// Un glisser-déposer produit naturellement `left: 342px`, et c'est précisément ce qui casse
// la responsivité. Les trois gestes rendent donc trois grandeurs sans unité d'écran :
//
//   milieu    → `object-position: 62% 38%`   quelle partie de la photo reste visible
//   coin      → `width: 70%`                 du conteneur
//   bord bas  → `aspect-ratio: 16 / 9`       la hauteur, relative à la largeur
//
// Les trois suivent la largeur de l'écran tout seuls : c'est une propriété du CSS, pas une
// précaution de notre part. Aucun recalcul au redimensionnement de la fenêtre.
//
// ── CE MODULE NE TOUCHE À AUCUN DOM ─────────────────────────────────────────
//
// Tout est pur : des nombres entrent, des nombres sortent. C'est ce qui permet de l'éprouver
// entièrement, et c'est ici que l'erreur serait SILENCIEUSE — une image qui glisse trois fois
// trop vite a l'air d'un caprice du navigateur, pas d'une division par la mauvaise longueur.
//
// ── LA FORMULE, RELEVÉE À LA SOURCE LE 2026-10-05 ───────────────────────────
//
// MDN, `background-position` — même règle de pourcentage que `object-position` :
//
//   « (container width - image width) × (position x%) = (x offset value) »
//
// Donc un pourcentage ne dit PAS où est le doigt : il dit quelle fraction du DÉBORDEMENT a
// défilé. Et le cas limite est documenté lui aussi :
//
//   « if your background-size is equal to the container size for a given axis, then a
//     percentage position for that axis will have no effect »

export type Size = { readonly width: number; readonly height: number };
/** Le point de la photo qui reste visible, en pourcentages — ce que porte `object-position`. */
export type Focal = { readonly x: number; readonly y: number };

/** Les largeurs sur lesquelles le geste s'aimante, en pourcentage du conteneur.
 *
 *  POURQUOI UN AIMANT : c'est ce qui sépare un résultat qui a l'air voulu d'un 63 % bancal.
 *  Les crans sont des fractions simples — moitié, tiers, deux tiers, trois quarts — celles
 *  qu'une grille utilise déjà. */
export const WIDTH_STOPS = [25, 33, 50, 66, 75, 100] as const;

/** Le plancher de largeur. Sous ça, ce n'est plus une photo, c'est une vignette perdue. */
export const WIDTH_MIN_PCT = 10;

/** Les ratios connus, dans l'ordre croissant. Carré, photo, 3:2, large. */
export const RATIO_STOPS = [1, 4 / 3, 3 / 2, 16 / 9] as const;

/** Les bornes du ratio, et c'est LE garde-fou de la responsivité : un ratio de 0,1 donne, à
 *  390 px de large, une image de 3 900 px de haut — atteignable d'un seul geste, et personne
 *  n'en veut. */
export const RATIO_MIN = 0.5;
export const RATIO_MAX = 4;

/** La tolérance de l'aimant, dans l'unité de la grandeur visée. */
const AIMANT_LARGEUR_PCT = 4;
const AIMANT_RATIO = 0.12;

/** Un nombre utilisable, ou le repli. Les mesures viennent du DOM : une image pas encore
 *  chargée vaut 0×0, un conteneur replié aussi, et un NaN se propagerait dans le style. */
const sain = (n: number, repli = 0): number => (typeof n === "number" && Number.isFinite(n) ? n : repli);

const borner = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

/**
 * PURE. De combien l'image dépasse son cadre, par axe, en pixels — sous `object-fit: cover`.
 *
 * `cover` prend la PLUS GRANDE des deux échelles, donc l'image couvre le cadre et dépasse sur
 * au plus un axe. Le débordement est ce qui reste à faire défiler : c'est lui que le
 * pourcentage parcourt, et c'est pour ça que tout part de cette fonction.
 */
export function coverOverflow(box: Size, natural: Size): Size {
  const bw = sain(box.width), bh = sain(box.height);
  const nw = sain(natural.width), nh = sain(natural.height);
  if (bw <= 0 || bh <= 0 || nw <= 0 || nh <= 0) return { width: 0, height: 0 };
  const echelle = Math.max(bw / nw, bh / nh);
  return {
    width:  Math.max(0, nw * echelle - bw),
    height: Math.max(0, nh * echelle - bh),
  };
}

/**
 * PURE. Quels axes peuvent bouger.
 *
 * Montrer une poignée horizontale sur une image qui ne déborde pas en largeur, c'est promettre
 * un geste sans effet : l'utilisateur tire, rien ne bouge, il croit à une panne. C'est le cas
 * que MDN documente — « will have no effect ».
 */
export function movableAxes(box: Size, natural: Size): { readonly x: boolean; readonly y: boolean } {
  const o = coverOverflow(box, natural);
  return { x: o.width > 0, y: o.height > 0 };
}

/**
 * PURE. Le nouveau point visible après un glissement de `by` pixels.
 *
 * L'IMAGE SUIT LE DOIGT, comme une carte : tirer vers la gauche découvre la droite, donc le
 * pourcentage MONTE. Inversé, le geste paraît cassé sans qu'on sache dire pourquoi.
 *
 * La vitesse vient du DÉBORDEMENT, jamais de la taille du cadre — c'est la faute classique, et
 * elle rend l'image trop lente sur une photo très large et trop rapide sur une presque carrée.
 */
export function dragFocal(from: Focal, by: { dx: number; dy: number }, box: Size, natural: Size): Focal {
  const o = coverOverflow(box, natural);
  const axe = (depart: number, d: number, debordement: number): number => {
    const p = borner(sain(depart, 50), 0, 100);
    // Aucun débordement ⇒ aucun effet possible : on rend la valeur de départ telle quelle.
    if (debordement <= 0) return Math.round(p);
    return Math.round(borner(p - (sain(d) / debordement) * 100, 0, 100));
  };
  return { x: axe(from.x, by.dx, o.width), y: axe(from.y, by.dy, o.height) };
}

/**
 * PURE. La valeur aimantée au cran le plus proche, ou la valeur telle quelle.
 *
 * LE PLUS PROCHE, pas le premier rencontré : avec des crans inégalement espacés, « le premier
 * dans la tolérance » saute au mauvais. Une tolérance nulle désactive l'aimant — c'est le mode
 * libre, et il n'a pas besoin d'un second chemin de code.
 */
export function snapTo(value: number, stops: readonly number[], tolerance: number): number {
  let meilleur = value;
  let ecart = Infinity;
  for (const s of stops) {
    const d = Math.abs(value - s);
    if (d <= tolerance && d < ecart) { ecart = d; meilleur = s; }
  }
  return meilleur;
}

/**
 * PURE. La nouvelle largeur, en pourcentage du CONTENEUR.
 *
 * En pourcentage du conteneur et de rien d'autre : c'est ce qui fait qu'une photo garde sa
 * place quand la page rétrécit. Bornée à 100 % — au-delà l'élément provoque un défilement
 * latéral, ce que l'audit des largeurs refuse.
 */
export function resizeWidth(startPct: number, dx: number, containerWidth: number, free: boolean): number {
  const largeur = sain(containerWidth);
  const depart = borner(sain(startPct, WIDTH_MIN_PCT), WIDTH_MIN_PCT, 100);
  if (largeur <= 0) return Math.round(depart);
  const brut = depart + (sain(dx) / largeur) * 100;
  const aimante = snapTo(brut, WIDTH_STOPS, free ? 0 : AIMANT_LARGEUR_PCT);
  return Math.round(borner(aimante, WIDTH_MIN_PCT, 100));
}

/**
 * PURE. Le nouveau ratio largeur/hauteur après un glissement du bord bas de `dy` pixels.
 *
 * TIRER VERS LE BAS GRANDIT LA HAUTEUR, donc BAISSE le ratio — un ratio qui monterait en
 * tirant vers le bas rendrait le geste incompréhensible.
 *
 * Le résultat n'est PAS arrondi à l'entier : un ratio est une fraction, et 1,78 n'est pas 2.
 */
export function resizeRatio(dy: number, widthPx: number, startHeightPx: number, free: boolean): number {
  const w = sain(widthPx), h0 = sain(startHeightPx);
  if (w <= 0 || h0 <= 0) return borner(RATIO_STOPS[0]!, RATIO_MIN, RATIO_MAX);
  const h = h0 + sain(dy);
  // Une hauteur nulle ou négative est le bout du geste : on rend la borne, pas une division
  // par zéro.
  if (h <= 0) return RATIO_MAX;
  const aimante = snapTo(w / h, RATIO_STOPS, free ? 0 : AIMANT_RATIO);
  return borner(aimante, RATIO_MIN, RATIO_MAX);
}

// ── QUELLE POIGNÉE LE DOIGT A-T-IL ATTRAPÉE ? ───────────────────────────────
//
// Trois gestes, trois endroits distincts, et c'est une décision de design : on ne devine
// jamais ce qu'un glissement va faire. Le MILIEU déplace, le COIN bas-droit redimensionne, le
// BORD BAS change le ratio.
//
// TROIS ET PAS HUIT. Un coin haut-gauche qui redimensionnerait demanderait de décider ce que
// « redimensionner vers le haut » signifie dans un flux responsive — et il n'y a pas de réponse
// claire. Les côtés et le bord haut déplacent, comme le milieu.

/** La taille d'une zone de poignée, en pixels d'écran.
 *
 *  28 : au-dessus du minimum de 24 px de WCAG 2.5.8, la règle que l'audit des largeurs applique
 *  déjà aux cibles de l'interface. Plus petit, c'est inatteignable au doigt. */
export const GRIP_PX = 28;

/** Ce qu'un glissement va faire. */
export type Grip = "move" | "size" | "ratio";

export type Rect = { readonly x: number; readonly y: number; readonly width: number; readonly height: number };

/**
 * PURE. La poignée sous ce point, ou `null` hors du cadre.
 *
 * LES ZONES SE RÉDUISENT AVEC LA PHOTO. Une zone fixe de 28 px avale entièrement une vignette
 * de 36 px : il ne reste plus un pixel pour déplacer, et le geste le plus courant devient
 * impossible sans que rien ne l'explique. Chaque zone est donc bornée au tiers de la dimension.
 *
 * LE COIN GAGNE SUR LE BORD, parce qu'ils se chevauchent et que l'ordre doit être dit une fois
 * ici plutôt que découvert à l'usage.
 */
export function gripAt(point: { x: number; y: number }, rect: Rect): Grip | null {
  const px = sain(point.x, NaN), py = sain(point.y, NaN);
  const w = sain(rect.width), h = sain(rect.height);
  const rx = sain(rect.x), ry = sain(rect.y);
  if (!Number.isFinite(px) || !Number.isFinite(py) || w <= 0 || h <= 0) return null;
  if (px < rx || px > rx + w || py < ry || py > ry + h) return null;
  // Au tiers de la dimension au plus : c'est ce qui garde une zone de déplacement sur une
  // vignette, et ça ne change rien sur une grande photo.
  const zoneX = Math.min(GRIP_PX, w / 3);
  const zoneY = Math.min(GRIP_PX, h / 3);
  const prochebas   = py >= ry + h - zoneY;
  const prochedroit = px >= rx + w - zoneX;
  if (prochebas && prochedroit) return "size";
  if (prochebas) return "ratio";
  return "move";
}

// ── UN GESTE → UNE SEULE GRANDEUR ───────────────────────────────────────────
//
// Les trois conversions sont au-dessus. Ce qui se décide ici est l'AIGUILLAGE, et une règle qui
// compte autant que les formules : UN GESTE NE TOUCHE QU'À SA GRANDEUR.
//
// Si déplacer renvoyait aussi une largeur, recadrer une photo écraserait la taille choisie
// avant — et l'opérateur verrait sa photo rétrécir en la recadrant, sans comprendre pourquoi.
// Chaque poignée lit donc UN seul axe du glissement : un doigt ne va jamais droit, et lire les
// deux ferait changer deux choses à la fois.

/** Ce qu'un geste rend : exactement UNE des trois grandeurs.
 *
 *  Un type local plutôt que `OverridePatch` de visual_doc : celui-là importe CE module pour ses
 *  bornes, et l'importer en retour ferait un cycle. L'appelant transpose. */
export type GestureChange =
  | { readonly focal: Focal }
  | { readonly widthPct: number }
  | { readonly ratio: number };

/** L'état au moment où le doigt se pose. Tout vient du DOM, donc rien n'est supposé sain. */
export type GestureStart = {
  readonly grip: Grip;
  /** Le point visible actuel, pour que le déplacement reprenne là où il en était. */
  readonly focal: Focal;
  /** La largeur rendue, en pourcentage du conteneur. */
  readonly widthPct: number;
  /** La boîte rendue de l'image, et sa taille intrinsèque — pour le débordement. */
  readonly box: Size;
  readonly natural: Size;
  readonly containerWidth: number;
};

/**
 * PURE. La grandeur que ce glissement produit.
 *
 * `free` désactive l'aimant (le mode « valeur libre »), et il est transmis tel quel : un second
 * aimant ici aurait sa propre tolérance, et les deux auraient divergé.
 */
export function gestureUpdate(start: GestureStart, delta: { dx: number; dy: number }, free: boolean): GestureChange {
  if (start.grip === "move") {
    return { focal: dragFocal(start.focal, delta, start.box, start.natural) };
  }
  if (start.grip === "size") {
    // `dx` SEUL : lire `dy` ferait changer le ratio en même temps que la largeur.
    return { widthPct: resizeWidth(start.widthPct, delta.dx, start.containerWidth, free) };
  }
  // `dy` SEUL, pour la raison symétrique.
  return { ratio: resizeRatio(delta.dy, start.box.width, start.box.height, free) };
}

// ── CE QUE L'OPÉRATEUR LIT PENDANT LE GLISSEMENT ────────────────────────────
//
// Sa demande, le 2026-10-05 : « when you put it an overlay appear with percentage ».
//
// LE TEXTE EST UNE DÉCISION, pas une mise en forme. Un ratio de 1,7777777777777777 ne dit rien
// à personne ; « 16:9 » dit tout. Et c'est la seule chose qui rend VISIBLE la promesse de
// responsivité : l'opérateur lit un pourcentage, donc il voit que ce n'est pas un pixel.

/** Les ratios connus et leur nom. Les mêmes valeurs que `RATIO_STOPS`, nommées — un nom par
 *  cran, dans le même ordre, pour qu'aucun cran ne soit sans nom. */
const RATIO_NAMES: ReadonlyArray<readonly [number, string]> = [
  [1, "1:1"], [4 / 3, "4:3"], [3 / 2, "3:2"], [16 / 9, "16:9"],
];

/** Assez près d'un cran pour porter son nom. Le glissement libre passe forcément à côté, et
 *  afficher « 1.8:1 » juste à côté de « 16:9 » ferait croire à deux réglages différents. */
const NOM_PROCHE = 0.02;

/** PURE. Le texte à montrer près du doigt pendant un geste. */
export function readoutFor(change: GestureChange): string {
  if ("focal" in change) return `${change.focal.x}% · ${change.focal.y}%`;
  if ("widthPct" in change) return `${change.widthPct}%`;
  for (const [v, nom] of RATIO_NAMES) if (Math.abs(change.ratio - v) <= NOM_PROCHE) return nom;
  return `${change.ratio.toFixed(1)}:1`;
}
